import React, { useState, useEffect, useRef, useMemo } from "react";
import { Tab, Nav } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { 
  getWeeklySummaryReport,
  clearWeeklySummaryReport,
  clearMonthlyDetailedTimesheetReport,
  getMonthlyDetailedTimesheetReport,
  getTimesheetCategorySummaryReport,
  clearTimesheetCategorySummaryReport,
  exportHandler,
  exportTaskTimesheetReports,
  showGeneralError,
} from "actions";
import { DownloadReport, DownloadTaskReport, CategorySummaryList, DetailList, Filter, Header, WeeklyBillingList } from "./component";
import { Excel } from "CommonComponent";
import { useLocation } from 'react-router-dom';
import './MonthlyBilling.css'
import moment from "moment-timezone";
import { filter } from "lodash";

const MonthlyBilling = ({
  embeddedView = false,
  hideExportActions = false,
  invoiceMode = false,
  fixedProgramIds = [],
  fixedMonthKey = "",
}) => {
  
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((s) => s.session);
  const weeklySummaryData = useSelector(
    (state) => state.timesheetReportsState?.weeklySummary?.data || []
  );
  const monthlyDetailedData = useSelector(
    (state) => state.timesheetReportsState?.monthlyDetailed?.data || []
  );
  const categorySummaryData = useSelector(
    (state) => state.timesheetReportsState?.categorySummary?.data || []
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const downloadZipPermissions = filter(session.user_permissions_raw, function(item){
    if(!isEmpty(item)){
      return item.object_code === 'DownloadTimesheetButton';
    }
  });

  const [activeKey, setActiveKey] = useState("weekly");

  const resolveInitialFilters = () => {
    const hasFixedMonth = /^\d{4}-\d{2}$/.test(String(fixedMonthKey || ""));
    const initialMoment = hasFixedMonth
      ? moment(`${fixedMonthKey}-01`, "YYYY-MM-DD")
      : moment();

    return {
      year: initialMoment.format("YYYY"),
      month: initialMoment.format("M"),
      selectedDate: initialMoment.toDate(),
      formatDate: initialMoment.format("YYYY-MM"),
      program: invoiceMode ? fixedProgramIds : [],
      task_ids: [],
    };
  };

  const [extraFiltersState, setExtraFiltersState] = useState({
      values: resolveInitialFilters()
  });
  const [isTaskReportDownloading, setIsTaskReportDownloading] = useState(false);
  const hasAutoLoadedRef = useRef(false);

  const taskOptions = useMemo(() => {
    const sourceBuckets = [
      weeklySummaryData,
      monthlyDetailedData,
      categorySummaryData,
    ];

    const tasks = sourceBuckets
      .flat()
      .map((row) => String(row?.category_task || row?.task || "").trim())
      .filter(Boolean);

    return Array.from(new Set(tasks)).sort((a, b) => a.localeCompare(b));
  }, [weeklySummaryData, monthlyDetailedData, categorySummaryData]);

  useEffect(() => {
    if (!invoiceMode) return;

    setExtraFiltersState((prev) => {
      const nextValues = {
        ...prev.values,
        ...resolveInitialFilters(),
        program: fixedProgramIds,
        task: prev.values?.task || "",
      };

      if (JSON.stringify(prev.values) === JSON.stringify(nextValues)) {
        return prev;
      }

      return {
        ...prev,
        values: nextValues,
      };
    });
  }, [invoiceMode, fixedMonthKey, JSON.stringify(fixedProgramIds)]);

  useEffect(() => {
      const canAutoLoad =
        embeddedView ||
        session?.current_page_permissions?.page_url === location.pathname;

      if (!canAutoLoad || hasAutoLoadedRef.current) {
        return;
      }

      hasAutoLoadedRef.current = true;
      handleFilter();
    }, [session?.current_page_permissions?.page_url, embeddedView, location.pathname]);

  const handleFilter = () => {
    //if(activeKey === 'weekly'){
      handleWeeklySummaryFilter();
    //}
    //else if(activeKey === 'detail'){
      handleMonthlyDetailFilter();
    //}
    //else if(activeKey === 'CategorySummary'){
      handleCategorySummaryFilter();
    //}
  }

  const handleWeeklySummaryFilter = () => {
    if (!isEmpty(extraFiltersState.values.formatDate)) {
      dispatch(getWeeklySummaryReport({ ...extraFiltersState.values, object_viewed_id }));
    } else {
      dispatch(clearWeeklySummaryReport());
    }
  };

  const handleMonthlyDetailFilter = () => {
    if (!isEmpty(extraFiltersState.values.formatDate)) {
      dispatch(getMonthlyDetailedTimesheetReport({ ...extraFiltersState.values, object_viewed_id }));
    } else {
      dispatch(clearMonthlyDetailedTimesheetReport());
    }
  };

  const handleCategorySummaryFilter = () => {
    if (!isEmpty(extraFiltersState.values.formatDate)) {
      dispatch(getTimesheetCategorySummaryReport({ ...extraFiltersState.values, object_viewed_id }));
    } else {
      dispatch(clearTimesheetCategorySummaryReport());
    }
  };

  const handleTimeByProgramExport = () => {
    const { year, month } = extraFiltersState.values;
    const date = new Date(year, month - 1); // month is 0-indexed for Date object
    const monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date).toUpperCase();
    const fileName = `monthly_summary_${monthName}_${year}.zip`;
    
    dispatch(exportHandler(
        'timesheet/reports/monthly-program/export', 
        extraFiltersState.values, 
        object_viewed_id, 
        fileName
    ));
  };

  const handleExport = () => {
      dispatch(exportHandler(
          'timesheet/reports/combined-monthly-reports/export', 
          extraFiltersState.values, 
          object_viewed_id, 
          'monthly_summary.xlsx'
      ));
  };

  const handleTaskReportExport = async () => {
    const { year, month, formatDate, program, task_ids } = extraFiltersState.values;

    if (!formatDate || !year || !month) {
      dispatch(showGeneralError('Please select a Period / Month.'));
      return;
    }

    if (!task_ids || task_ids.length === 0) {
      dispatch(showGeneralError('Please select at least one Task.'));
      return;
    }

    const fileName = `task_timesheet_reports_${year}_${month}.zip`;
    const payload = {
      month: Number(month),
      year: Number(year),
      task_ids,
      program: program || [],
      object_viewed_id,
    };

    setIsTaskReportDownloading(true);
    try {
      await dispatch(exportTaskTimesheetReports(payload, fileName));
    } finally {
      setIsTaskReportDownloading(false);
    }
  };

  return (
    <div className="wrapper-div">
      {!embeddedView && <Header />}
      {!hideExportActions && (
        <div className="d-flex justify-content-end align-items-center gap-3 p-4">
          {(!isEmpty(downloadZipPermissions) && downloadZipPermissions[0]['rights_view'] == 1) && (
            <>
              <DownloadReport handleTimeByProgramExport={handleTimeByProgramExport} />
              <DownloadTaskReport
                handleTaskReportExport={handleTaskReportExport}
                isDownloading={isTaskReportDownloading}
              />
            </>
          )}
          <Excel handleExport={handleExport} />
        </div>
      )}
        <Filter
          onFilter={handleFilter}
          extraFiltersState={extraFiltersState}
          setExtraFiltersState={setExtraFiltersState}
          hidePeriodPicker={invoiceMode}
          hideProgramDropdown={invoiceMode}
          showTaskFilter={invoiceMode}
          showTaskMultiSelect={!invoiceMode}
          taskOptions={taskOptions}
        />
      <div className="m-4">
        <div className="view-container">
          <Tab.Container
            activeKey={activeKey}
            onSelect={(k) => setActiveKey(k)}
          >
            <div className="p-4">
              {/* Tabs Header */}
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="weekly">Weekly</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="detail">Detail</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="CategorySummary">
                    Category Summary
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            {/* Tabs Content */}
            <Tab.Content className="mt-3">
              <Tab.Pane eventKey="weekly">
                <WeeklyBillingList
                  extraFiltersState={extraFiltersState}
                  setExtraFiltersState={setExtraFiltersState}
                  taskFilterValue={extraFiltersState?.values?.task || ""}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="detail">
                <DetailList
                  handleFilter={handleFilter}
                  taskFilterEnabled={invoiceMode}
                  taskFilterValue={extraFiltersState?.values?.task || ""}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="CategorySummary">
                <CategorySummaryList taskFilterValue={extraFiltersState?.values?.task || ""} />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default MonthlyBilling;
